import { ReactNode } from "react";
import {
  TreeItemProps as AriaTreeItemProps,
  Button as ReactAriaButton,
  Collection as ReactAriaCollection,
  Tree as ReactAriaTree,
  TreeItem as ReactAriaTreeItem,
  TreeItemContent as ReactAriaTreeItemContent,
  TreeProps,
} from "react-aria-components";

import { cn, cv } from "../utils";
import { NcCheckbox } from "./nc-checkbox";
import { NcIconChevronRight } from "./nc-icons";

const chevronVariants = cv(
  "w-4.5 h-4.5 transition-transform duration-200 ease-in-out data-drop-target:outline-active data-drop-target:outline-2 data-drop-target:bg-active/25",
  {
    variants: {
      isExpanded: {
        true: "transform rotate-90",
      },
      isDisabled: {
        true: "text-disabled",
      },
    },
  }
);

export type NcTreeProps<T extends object> = Omit<TreeProps<T>, "selectionBehavior">;

export const NcTree = <T extends object>({ children, className, ...props }: NcTreeProps<T>) => {
  return (
    <ReactAriaTree
      data-nc="NcTree"
      className={cn(
        "data-focus-visible:outline-active relative max-w-full focus:outline-0 data-empty:py-3 data-empty:italic data-focus-visible:outline-2",
        className
      )}
      {...props}
    >
      {children}
    </ReactAriaTree>
  );
};

export interface NcTreeItemProps extends Omit<AriaTreeItemProps, "textValue" | "children"> {
  label: string;
  /** Rendered before the label, e.g. a file or folder icon. */
  icon?: ReactNode;
  /** Rendered after the label, e.g. buttons acting on the item. */
  actions?: ReactNode;
  /** Nested items, usually an `NcTree.Collection`. */
  children?: ReactNode;
}

const NcTreeItem = ({
  label,
  icon,
  actions,
  className,
  id,
  children,
  ...props
}: NcTreeItemProps) => {
  return (
    <ReactAriaTreeItem
      data-nc="NcTree.Item"
      className={cn(
        "group bg-light relative flex gap-3 px-1 py-1.5 transition-colors duration-150 select-none",
        "aria-disabled:bg-disabled-light aria-disabled:text-disabled aria-disabled:cursor-not-allowed",
        "data-focus-visible:outline-active outline-0 data-focus-visible:z-1 data-focus-visible:outline-2",
        "data-drop-target:outline-active data-drop-target:bg-active/25 data-drop-target:z-1 data-drop-target:outline-2",
        "data-dragging:opacity-33",
        "data-hovered:bg-ui-light data-href:cursor-pointer",
        className
      )}
      textValue={label}
      id={id}
      {...props}
    >
      <ReactAriaTreeItemContent>
        {({
          selectionMode,
          selectionBehavior,
          hasChildItems,
          isExpanded,
          isDisabled,
          allowsDragging,
        }) => {
          const showCheckbox = selectionMode !== "none" && selectionBehavior === "toggle";
          return (
            <div className="flex w-full min-w-0 items-center gap-1.5">
              {allowsDragging && (
                <ReactAriaButton slot="drag">
                  <GripIcon />
                </ReactAriaButton>
              )}
              {showCheckbox && <NcCheckbox slot="selection" />}
              <div className="w-[calc(calc(var(--tree-item-level)_-_1)_*_calc(var(--spacing)_*_3))] shrink-0" />
              {hasChildItems ? (
                <ReactAriaButton slot="chevron" className="cursor-pointer">
                  <NcIconChevronRight
                    aria-hidden
                    className={chevronVariants({ isExpanded, isDisabled })}
                  />
                </ReactAriaButton>
              ) : (
                <div className="w-4.5 shrink-0" />
              )}
              {icon}
              <span className="min-w-0 truncate">{label}</span>
              {actions && <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div>}
            </div>
          );
        }}
      </ReactAriaTreeItemContent>
      {children}
    </ReactAriaTreeItem>
  );
};

const GripIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="9" cy="12" r="1"></circle>
    <circle cx="9" cy="5" r="1"></circle>
    <circle cx="9" cy="19" r="1"></circle>
    <circle cx="15" cy="12" r="1"></circle>
    <circle cx="15" cy="5" r="1"></circle>
    <circle cx="15" cy="19" r="1"></circle>
  </svg>
);

NcTree.Item = NcTreeItem;
NcTree.Collection = ReactAriaCollection;
